import React, { useEffect, useRef } from 'react';
import styles from './Webview.module.css';
import { useLanguage } from 'src/hooks/useLanguage/useLanguage';
import { sendError, sendLoadingCompleted} from 'src/services/event-message/event-message.service';
import { useAppDispatch, useAppSelector } from 'src/hooks/hooks';
import { selectWebView, selectWebViewFile } from 'src/store/webview/webviewSelects';
import { shallowEqual } from 'react-redux';
import { LoadingStatus } from 'src/enums/webview-loading-status';
import { setLoadingState } from 'src/store/loader/loaderSlice';
import { getWebviewFileAsync } from 'src/store/webview/actionWebView';
import { selectWebViewFileUrl } from 'src/store/design/designSelects';
import { useTranslation } from 'react-i18next';
import metricsService, {
    DISPLAY_DESIGN,
    FAILED,
    LOAD_WEBVIEW,
    SUCCEEDED,
} from 'src/services/metrics/metrics.service';
import { ErrorCode } from 'src/enums/error-code.enum';
import biLoggingService, {
    ErrorReportData,
    SeverityType,
    StageReportData,
} from 'src/services/bi-logging/bi-logging.service';
import { useListener } from 'src/hooks/useListener/useListener';

export function Webview() {
    const dispatch = useAppDispatch();
    const webview = useAppSelector(selectWebView, shallowEqual);
    const downloadUrl = useAppSelector(selectWebViewFileUrl, shallowEqual);
    const webviewFile = useAppSelector(selectWebViewFile, shallowEqual);
    const { t } = useTranslation();
    const webviewIFrame = useRef<HTMLIFrameElement>(null);
    const language = useLanguage();

    useEffect(() => {
        dispatch(getWebviewFileAsync(downloadUrl));
    }, []);

    useEffect(() => {
        if (webviewFile) {
            initWebView(webviewFile);
        }
    }, [webviewFile]);

    useEffect(() => {
        webview.changeLanguage(language);
    }, [language]);

    const handleClick = () => {
        webviewIFrame.current.focus();
    };

    useListener(webviewIFrame?.current?.contentDocument, handleClick);

    const initWebView = (webviewFile: ArrayBuffer) => {
        try {
            metricsService.setDimensionInfo(LOAD_WEBVIEW);
            webview.setInlineFrame(webviewIFrame.current);
            webview.closeFileLoadingDialog();
            webview.setErrorListener((errorTitle: string, errorMessage: string) => {
                console.log(`${errorTitle} : ${errorMessage}`);
                errorHandler();
            });

            webview.setOnLoadStartListener(() => {
                dispatch(setLoadingState(LoadingStatus.Start));
                console.log('StartLoad');
            });

            webview.setOnLoadEndListener(() => {
                sendLoadingCompleted();
                dispatch(setLoadingState(LoadingStatus.End));
                metricsService.sendMetrics(LOAD_WEBVIEW, SUCCEEDED);
                metricsService.sendMetrics(DISPLAY_DESIGN, SUCCEEDED);

                const stageReportData: StageReportData = {
                    object: 'design',
                    stage: 'opening end',
                };
                biLoggingService.sendStageReport(stageReportData);

                console.log('endLoad');
            });

            webview.loadDentalCADWebFileAsArrayBuffer(webviewFile);
            webview.changeLanguage(language);
        } catch (error) {
            console.log(error);
            errorHandler();
        }
    };

    const errorHandler = () => {
        metricsService.sendMetrics(LOAD_WEBVIEW, FAILED);
        const errorData: ErrorReportData = {
            object: ErrorCode.WebviewInit,
            code: '',
            description: ErrorCode.WebviewInit,
            severity: SeverityType.Error,
        };
        biLoggingService.sendErrorReport(errorData);

        sendError({
            errorCode: ErrorCode.WebviewInit,
            errorHeader: t('SharedDesignError.Retryable.Text'),
        });
    };

    return (
        <iframe
            auto-id="e3dv_webview"
            title="webview"
            className={styles.container}
            ref={webviewIFrame}></iframe>
    );
}
